/**
 * How a link inside a chat answer renders.
 *
 * Three kinds arrive, and they are not interchangeable:
 *   trodo://cite/N     a source marker — a superscript the reader hovers to
 *                      see the claim a sentence rests on;
 *   trodo://entity/…   an in-app record — a tag that opens the entity drawer;
 *   everything else    plain text, because a backend path or a referrer URL
 *                      dressed as a link invites a click that goes nowhere.
 *
 * Lifted out of ChatPage so it can be tested. It could not be, there: ChatPage
 * imports react-router, which does not resolve under the test runner, so the
 * one path every citation in the product travels through had zero coverage —
 * and a whole deep-research report shipped its sources to the reader as the
 * glued-together text "1415".
 */
import React from "react";
import { openUserProfile } from "features/users/openUserProfile";

/**
 * The text a node renders, however React chose to wrap it.
 *
 * The old version handled a string and an array of strings, and called
 * String() on anything else — so a link whose child arrived as a React
 * element became the label "[object Object]", failed the "is this a bare
 * number" test, and fell through to the plain-text branch. That is the whole
 * reason a report's sources reached the reader as "1415": the href was
 * `trodo://cite/14` the entire time and the label was unreadable. Walking
 * children recursively costs nothing and cannot be surprised by wrapping.
 */
export function markdownCodeChildrenToString(children) {
  if (children == null || typeof children === "boolean") return "";
  if (typeof children === "string") return children;
  if (typeof children === "number") return String(children);
  if (Array.isArray(children)) return children.map(markdownCodeChildrenToString).join("");
  if (typeof children === "object" && children.props) {
    return markdownCodeChildrenToString(children.props.children);
  }
  return "";
}

/**
 * A citation with no record behind it opens the report's Sources list at its
 * number. Found from the click itself: several reports in one thread each
 * number their sources from 1.
 */
export function showSource(el, n) {
  const answer = el && el.closest ? el.closest(".trodo-answer") : null;
  const list = answer && answer.querySelector(".trodo-answer-sources");
  const item = list && list.querySelector(`[data-source="${n}"]`);
  if (!item) return false;
  list.open = true;
  if (item.scrollIntoView) item.scrollIntoView({ block: "center", behavior: "smooth" });
  item.setAttribute("data-flash", "");
  setTimeout(() => item.removeAttribute("data-flash"), 1600);
  return true;
}

function openEntity(kind, id) {
  if (typeof window === "undefined") return;
  // Users open the app-wide drawer directly, so a user link works in the side
  // panel too, not only on the chat page that listens for the event below.
  if (kind === "user") { openUserProfile(id); return; }
  window.dispatchEvent(new CustomEvent("trodo:chat:openEntity", { detail: { kind, id } }));
}

const ENTITY_RE = /^trodo:\/\/entity\/([a-z_]+)\/(.+)$/i;

/** Record kinds the chat can open in place. Any other citation opens its source. */
export const OPENABLE_KINDS = new Set(["run", "span", "user", "group", "issue", "session", "event"]);

export default function ChatMarkdownLink({ children, href, title, ...props }) {
  const isTrodoEntity = typeof href === "string" && href.startsWith("trodo://entity/");
  const isCite = typeof href === "string" && href.startsWith("trodo://cite/");

  // A source marker: a bare number the report wrote to point at the claim a
  // sentence rests on. It renders as a superscript, carries the claim itself
  // on hover, and — when that claim named a record — opens it. Before this it
  // shipped as the literal text "[C13]", which told the reader evidence
  // existed and gave them no way to reach it.
  const label = markdownCodeChildrenToString(children).trim();
  // "2–8" or "11+3": a run of citations collapsed into one chip (AnswerMarkdown).
  const first = (label.match(/^(\d{1,3})(?:–\d{1,3}|\s?\+\d{1,3})?$/) || [])[1];
  if ((isCite || isTrodoEntity) && first) {
    const em = isTrodoEntity ? href.match(ENTITY_RE) : null;
    const kind = em ? em[1] : null;
    const id = em ? decodeURIComponent(em[2]) : null;
    return (
      <sup
        {...props}
        title={title || undefined}
        role="link"
        tabIndex={0}
        aria-label={`Source ${label}${title ? `: ${title}` : ""}`}
        onClick={(e) => {
          e.preventDefault(); e.stopPropagation();
          if (id && OPENABLE_KINDS.has(kind)) openEntity(kind, id);
          else showSource(e.currentTarget, first);
        }}
        className="trodo-chat-citation"
        style={{ cursor: "pointer" }}
      >
        {label}
      </sup>
    );
  }

  if (isTrodoEntity) {
    const m = href.match(ENTITY_RE);
    const kind = m ? m[1] : null;
    const id = m ? decodeURIComponent(m[2]) : null;
    return (
      <a
        {...props}
        href={href}
        // The visible label is often a shortened id (the answer says
        // "0a72ab20", the entity is the full UUID). The full value lives on
        // title + data-entity-id so it can be hovered, copied and found with
        // the browser's own find-in-page — a tag you cannot search for is
        // barely better than plain text.
        title={id ? `${kind}: ${id}` : undefined}
        data-entity-id={id || undefined}
        data-entity-kind={kind || undefined}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (kind && id) openEntity(kind, id); }}
        className="trodo-chat-entity-link trodo-chat-entity-tag"
      >
        {children}
      </a>
    );
  }

  // A web page opens in a new tab. Anything else that is not ours (a backend
  // path, a relative link, javascript:) stays text: a click that goes nowhere
  // is worse than no link.
  if (typeof href === "string" && /^https?:\/\/[^/\s]+\.[^/\s]+/i.test(href)) {
    return (
      <a {...props} href={href} title={title || href} target="_blank" rel="noopener noreferrer" className="trodo-chat-web-link" style={{ color: "rgb(var(--trodo-primary-rgb))", textDecoration: "underline" }}>
        {children}
      </a>
    );
  }
  return <span>{children}</span>;
}
